import React, { useEffect, useMemo, useRef, useState } from "react";
import MaterialTable from "material-table";
import { Paper } from "@material-ui/core";
import i18n, { t } from "i18next";
import { useQuery } from "react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import Button from "components/Button/Button";
import LoadCont from "components/LoadCont/LoadCont";
import TagLabel from "components/Tags/Label/TagLabel";
import useAccount from "classes/Accounts/hooks/useAccount";
import genRequest from "includes/request";
import { tableIcons, genTrads } from "components/Table/CustomTable";
import { convertSortParams } from "Pages/Contacts/employeeSort";
import { getUserId, roleLabel, siteLabel, professionsQuery, toProfessionsMap } from "./exportUtils";

/**
 * Contacts table (like the contacts page one) to pick the contacts to export.
 * The selection (`picked`, a Map id => user owned by the parent) is kept across pages and searches.
 */
export default function ContactsPickerTable({ picked: draft, setPicked: setDraft, initialSearch }) {
	const { account } = useAccount();
	const [isLoading, setIsLoading] = useState(true);
	// Kept here: the table resets its page size to the options one when its columns change
	// (ex: when the family / function labels arrive)
	const [pageSize, setPageSize] = useState(25);
	// Starts with the search of the contacts page, so that the table matches its results
	const [search, setSearch] = useState(initialSearch || "");
	const [debounced, setDebounced] = useState((initialSearch || "").trim());
	const tableRef = useRef();
	// Rows of the displayed page, for the "check all" checkbox of the header
	const pageRows = useRef([]);
	// The table reads the checked state when loading a page: keep the latest draft for it
	const draftRef = useRef(draft);
	draftRef.current = draft;

	const { data: sites } = useQuery(
		["Sites", "SitesOfCompany", account.CompanyId],
		() => genRequest("Sites/SitesOfCompany/" + account.CompanyId)
	);
	const { data: groups } = useQuery(
		["Groups", "OfCompany", account.CompanyId],
		() => genRequest("Groups/OfCompany/" + account.CompanyId)
	);

	// Family / function labels, missing from the filtered request: loaded in the background,
	// the rows are displayed without waiting for them (the cells fill in when they arrive)
	const profQuery = professionsQuery(genRequest, account.CompanyId);
	const { data: profContacts } = useQuery(profQuery.queryKey, profQuery.queryFn, { staleTime: profQuery.staleTime });
	const professions = useMemo(() => toProfessionsMap(profContacts), [profContacts]);

	useEffect(() => {
		const tm = setTimeout(() => setDebounced(search.trim()), 300);
		return () => clearTimeout(tm);
	}, [search]);

	// A new search reloads the table from its first page (the table loads itself on mount)
	const isFirstSearch = useRef(true);
	useEffect(() => {
		if (isFirstSearch.current) {
			isFirstSearch.current = false;
			return;
		}
		tableRef.current?.onQueryChange({ page: 0 });
	}, [debounced]);

	const fetchPage = (query) => {
		setIsLoading(true);
		// Sorted by last name by default, like the contacts page
		const { orderBy, orderDirection } = convertSortParams(
			query.orderBy?.field || "LastName",
			query.orderBy?.field ? query.orderDirection : "asc"
		);

		return genRequest("Employes/FilteredSortedContactEmployesWithGroup", {
			CompanyId: account.CompanyId,
			PageNumber: query.page + 1,
			PageSize: query.pageSize,
			OrderBy: orderBy,
			OrderByDirection: orderDirection,
			...(debounced ? { SearchedTerms: debounced } : {}),
		}, "post").then((resp) => {
			const rows = (resp?.Items || []).map((user) => ({
				...user,
				tableData: { checked: draftRef.current.has(getUserId(user)) },
			}));
			pageRows.current = rows;
			return ({
				data: rows,
				page: (resp?.CurrentPage || 1) - 1,
				totalCount: resp?.TotalCount || 0,
			});
		}).finally(() => setIsLoading(false));
	};

	const handleSelectionChange = (checkedRows, row) => {
		setDraft((prev) => {
			const next = new Map(prev);
			if (row) {
				// One row toggled
				if (row.tableData?.checked)
					next.set(getUserId(row), row);
				else
					next.delete(getUserId(row));
			} else if (checkedRows.length) {
				// Header checkbox: whole page checked
				pageRows.current.forEach((u) => next.set(getUserId(u), u));
			} else {
				// Header checkbox: whole page unchecked
				pageRows.current.forEach((u) => next.delete(getUserId(u)));
			}
			return (next);
		});
	};

	const clearDraft = () => {
		const empty = new Map();
		// Updated now: the table reloads the page before the next render
		draftRef.current = empty;
		setDraft(empty);
		pageRows.current.forEach((u) => {
			if (u.tableData)
				u.tableData.checked = false;
		});
		tableRef.current?.onQueryChange();
	};

	// Memoized: new columns / options make the table recompute its rows on each selection
	const columns = useMemo(() => {
		const sitesMap = new Map((sites || []).map((s) => [s.SiteId, s]));
		const groupsMap = new Map((groups || []).map((g) => [g.GroupId, g]));

		return [
		{ title: t("User.FIRST_NAME"), field: "FirstName" },
		{ title: t("User.LAST_NAME"), field: "LastName" },
		{ title: t("User.WORK_FAM"), field: "WorkFamillyId", render: (u) => u.WFY || professions.get(getUserId(u))?.WFY || "" },
		{ title: t("User.WORK_FUNC"), field: "WorkFunctionId", render: (u) => u.WFU || professions.get(getUserId(u))?.WFU || "" },
		{ title: t("User.ROLE"), field: "Role", render: (u) => <RoleTag role={u.Role} /> },
		{ title: t("User.SITE"), field: "SiteId", render: (u) => siteLabel(sitesMap.get(u.SiteId)) },
		{
			title: t("Contacts.EXPORT_GROUPS"),
			field: "groups",
			render: (u) => (
				<div className="d-flex flex-wrap gap-1">
					{(u.GroupsOfUser || []).map((id) => groupsMap.get(id)).filter((g) => g).map((g) => (
						<TagLabel key={g.GroupId} text={g.Name} color={g.DefaultHexaColor} />
					))}
				</div>
			),
		},
		];
	}, [sites, groups, professions]);

	// While loading, "No items to display" made people believe the list was empty:
	// nothing in the table, a loader over its visible area instead (a message in the table was
	// centered on its whole width, out of the screen on mobile)
	const localization = useMemo(() => {
		const trads = i18n.language !== "en-EN" ? genTrads() : {};
		if (!isLoading)
			return (trads);
		return ({ ...trads, body: { ...trads.body, emptyDataSourceMessage: "" } });
	}, [isLoading]);

	const components = useMemo(() => ({
		Container: (cprops) => <Paper {...cprops} className="Table selectable" />,
		Toolbar: () => null,
		// Its spinner is centered on the whole table width (out of the screen on mobile): own loader instead
		OverlayLoading: () => null,
	}), []);

	const options = useMemo(() => ({
		selection: true,
		showSelectAllCheckbox: true,
		showTextRowsSelected: false,
		toolbar: false,
		// Stable column widths: with "auto", they change on each checked row
		tableLayout: "fixed",
		sorting: true,
		thirdSortClick: false,
		draggable: false,
		emptyRowsWhenPaging: false,
		pageSize,
		pageSizeOptions: [10, 25, 50, 100],
		headerStyle: { position: "sticky", top: 0 },
		maxBodyHeight: "100%",
	}), [pageSize]);

	return (
		<div className="ContactsPickerTable">
			<div className="ContactsPickerTable-bar">
				<div className="ContactsPicker-search">
					<FontAwesomeIcon icon={faSearch} />
					<input
						type="search"
						value={search}
						autoFocus={true}
						placeholder={t("Contacts.EXPORT_SEARCH_PLACEHOLDER")}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
				<div className="d-flex align-items-center gap-3">
					<b>{t("Contacts.EXPORT_PICKED", { count: draft.size })}</b>
					{draft.size > 0 &&
						<Button className="unstyled ContactsExport-link w-auto" onClick={clearDraft}>
							{t("Contacts.EXPORT_CLEAR_SELECTION")}
						</Button>}
				</div>
			</div>

			<div className="ContactsPickerTable-table">
				{isLoading &&
					<div className="ContactsPickerTable-loading">
						<LoadCont width={30} text={t("Commons.LOADING")} />
					</div>}
				<MaterialTable
					tableRef={tableRef}
					title=""
					columns={columns}
					data={fetchPage}
					icons={tableIcons}
					localization={localization}
					onSelectionChange={handleSelectionChange}
					// A click on the row toggles its checkbox
					onRowClick={(e) => e.target.closest("tr")?.querySelector("input[type='checkbox']")?.click()}
					components={components}
					options={options}
					onChangeRowsPerPage={setPageSize}
				/>
			</div>
		</div>
	);
}

// Same tag as the role column of the contacts page
function RoleTag({ role }) {
	if (!role)
		return (false);
	const isAdminRole = ["SiteAdmin", "SuperAdmin"].includes(role);
	return (
		<TagLabel
			className={"user-role" + (role === "EloAdmin" ? " rainbow" : "")}
			color={isAdminRole ? "red" : "grey"}
			text={roleLabel(role)}
		/>
	);
}
